import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, TableCell, TableRow } from "@material-ui/core";
import { Table, Header, Controls } from "components/Table";
import { useDispatch, useSelector } from "react-redux";
import { SelectOptions, useProcessingStatus, useTableSort } from "hooks";
import { getTemplates, getTemplatesProcessingState } from "store/features/templates/overivew/overview.selectors";
import { TemplateEditor } from "components/TemplateEditor/TemplateEditor";
import { deleteTemplate, fetchTemplates } from "store/features/templates/overivew/overview.actions";
import { getAuthState } from "store/features/authorization";
import Chip from "@material-ui/core/Chip";
import { ChipTableCell } from "components";

const headCells = [
    { id: "name", numeric: false, disablePadding: false, label: "Template name" },
    { id: "groups", numeric: false, disablePadding: false, label: "Groups" },
];

export interface TemplateDetails {
    name: string;
    userGroups: SelectOptions[];
    auto?: boolean;
}

export interface TemplateEditorState {
    isNew?: boolean;
    isOpen: boolean;
    template: TemplateDetails;
}

export const TemplatesOverview: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [state, setState] = useState<TemplateEditorState>({
        isOpen: false,
        template: {
            name: "",
            userGroups: [],
        },
    });
    const { isProcessing, hasError, isDeleting } = useSelector(getTemplatesProcessingState);
    const templates = useSelector(getTemplates);
    const { sortedFields, order, orderBy, toggleSort } = useTableSort(templates, "name");
    const { isAuthorized } = useSelector(getAuthState);
    useProcessingStatus(isDeleting || isProcessing, hasError);

    useEffect(() => {
        if (isAuthorized) {
            dispatch(fetchTemplates.request());
        }
        // eslint-disable-next-line
    }, []);

    const handleDeleteTemplate = (templateName: string) => {
        dispatch(deleteTemplate.request(templateName));
    };

    const handleEditTemplate = (userGroups: SelectOptions[]) => (templateName: string) => {
        setState({
            isOpen: true,
            template: {
                name: templateName,
                userGroups,
                auto: false,
            },
        });
    };

    const handleStateReset = () =>
        setState({
            isOpen: false,
            template: {
                name: "",
                userGroups: [],
            },
        });

    const handleNewTemplate = () =>
        setState({
            isNew: true,
            isOpen: true,
            template: {
                name: "",
                userGroups: [],
            },
        });

    return (
        <>
            <Header title="E-mail templates">
                <Button variant="contained" color="primary" onClick={handleNewTemplate}>
                    {t("buttons.label.createNewTemplate")}
                </Button>
            </Header>
            <Box p={2}>
                <Table headCells={headCells} onSort={toggleSort} order={order} orderBy={orderBy}>
                    {sortedFields.map(({ name, userGroups }, index) => (
                        <TableRow key={`${name}_${index}`}>
                            <TableCell>{name}</TableCell>
                            <ChipTableCell padding="none">
                                {userGroups.map(({ name }, index) => (
                                    <Chip key={`${name}_${index}`} label={name} />
                                ))}
                            </ChipTableCell>
                            <TableCell align="right" padding="none">
                                <Box pr={2}>
                                    <Controls
                                        id={name}
                                        isCopyHidden={true}
                                        isProcessing={isDeleting}
                                        hasError={hasError}
                                        onEdit={handleEditTemplate(userGroups)}
                                        onDelete={handleDeleteTemplate}
                                    />
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}
                </Table>
            </Box>
            <TemplateEditor
                isNew={state.isNew}
                isOpen={state.isOpen}
                onStateReset={handleStateReset}
                templateDetails={state.template}
            />
        </>
    );
};
